import { useEffect, useRef } from 'react';

interface InstagramEmbedProps {
  postUrl: string;
  captioned?: boolean;
  maxWidth?: number;
  className?: string;
}

/**
 * A React component for embedding Instagram posts
 * 
 * @param postUrl - The URL of the Instagram post to embed
 * @param captioned - Whether to show the caption (default: true)
 * @param maxWidth - Maximum width of the embed in pixels (default: 540)
 * @param className - Additional CSS classes to apply to the container
 */
export const InstagramEmbed = ({ 
  postUrl, 
  captioned = true, 
  maxWidth = 540,
  className = ''
}: InstagramEmbedProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    // Function to load Instagram embed script
    const loadInstagramEmbed = () => {
      // Check if the script is already loaded
      if (window.instgrm) {
        window.instgrm.Embeds.process();
        return;
      }
      
      // Create script element
      const script = document.createElement('script');
      script.async = true;
      script.src = '//www.instagram.com/embed.js';
      script.onload = () => {
        if (window.instgrm) {
          window.instgrm.Embeds.process();
        }
      };
      
      document.body.appendChild(script);
      
      // Clean up function to remove script on unmount
      return () => {
        // We don't remove the script because it might be used by other components
      };
    };
    
    loadInstagramEmbed();
  }, [postUrl]);
  
  return (
    <div ref={containerRef} className={className}>
      <blockquote 
        className="instagram-media" 
        data-instgrm-captioned={captioned ? "true" : undefined}
        data-instgrm-permalink={postUrl}
        data-instgrm-version="14"
        style={{ 
          background: '#FFF', 
          border: '0', 
          borderRadius: '3px', 
          boxShadow: '0 0 1px 0 rgba(0,0,0,0.5),0 1px 10px 0 rgba(0,0,0,0.15)', 
          margin: '1px', 
          maxWidth: `${maxWidth}px`, 
          minWidth: '326px', 
          padding: '0', 
          width: '99.375%',
        }}
      >
        <div style={{ padding: '16px' }}>
          <a href={postUrl} style={{ background: '#FFFFFF', lineHeight: '0', padding: '0 0', textAlign: 'center', textDecoration: 'none', width: '100%' }} target="_blank" rel="noopener noreferrer">
            &nbsp;
          </a>
        </div>
      </blockquote>
    </div>
  );
};

// Add TypeScript declaration for window.instgrm
declare global {
  interface Window {
    instgrm?: {
      Embeds: {
        process: () => void;
      };
    };
  }
}

export default InstagramEmbed; 